// One disciplinary incident, and every step a manager takes on it: release as a
// written allegation or close it; record his response; decide, or refer it to a
// hearing. db/migrations/4401, docs/DISCIPLINARY-INCIDENTS.md.
//
// Each button calls one Postgres function, which decides whether the step is in
// order and who may take it. The card only shows what is open to the person
// looking at it, and shows the refusal sentence when there is one.
import { useState } from 'react'
import { ChevronDown, ChevronRight, Copy, FileText, Gavel, MessageSquare, Send, X } from 'lucide-react'
import {
  useCloseDisciplinaryIncident, useDecideDisciplinaryCase, useOpenDisciplinaryIncidentFile,
  useRecordDisciplinaryResponse, useReleaseDisciplinaryIncident,
} from '../../hooks/backend/disciplinary'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { day, todaySast, when } from '../../utils/disciplinary'
import {
  fileKind, incidentStage, NOTICE_METHODS, REQUESTED_LABEL, stepTone, type Incident,
} from '../../utils/disciplinaryIncidents'

type Open = null | 'release' | 'close' | 'respond' | 'decide'

export function IncidentCard({ i, onChanged, compact = false }: { i: Incident; onChanged: () => void; compact?: boolean }) {
  const release = useReleaseDisciplinaryIncident()
  const close = useCloseDisciplinaryIncident()
  const respond = useRecordDisciplinaryResponse()
  const decide = useDecideDisciplinaryCase()
  const openFile = useOpenDisciplinaryIncidentFile<{ url: string }>()
  const [open, setOpen] = useState<Open>(null)
  const [busy, setBusy] = useState(false)
  const [showDetail, setShowDetail] = useState(!compact)
  const [showLog, setShowLog] = useState(false)
  const [notice, setNotice] = useState({ notice_on: todaySast(), method: 'whatsapp', note: '' })
  const [reason, setReason] = useState('')
  const [resp, setResp] = useState({ responded_on: todaySast(), response: '' })
  const [dec, setDec] = useState({ decision: '', reasons: '' })
  const st = incidentStage(i)
  const s = i.suggestion
  const c = i.case

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      setOpen(null)
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const decisions = [
    { value: 'no_action', label: 'No action: the allegation is not proved, or a talk is enough' },
    { value: 'verbal_warning', label: 'Verbal warning' },
    ...(c && c.proposed_sanction !== 'verbal_warning'
      ? [{ value: 'refer_to_hearing', label: `Refer it to a hearing (the most it can decide: ${c.proposed_label.toLowerCase()})` }]
      : []),
    { value: 'withdrawn', label: 'Withdraw it: raised in error' },
  ]

  return (
    <article className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        {!compact && <span className="font-semibold text-foreground">{i.driver ?? 'Driver'}</span>}
        <span className="text-xs text-muted-foreground">{i.ref}</span>
        <Chip tone={st.tone}>{st.text}</Chip>
        {s && i.status !== 'closed' && (
          <Chip tone={s.applies ? stepTone(c?.proposed_sanction ?? s.step) : 'neutral'}>
            {s.applies ? `Suggested: ${c?.proposed_label ?? s.step_label}` : 'Coaching only'}
          </Chip>
        )}
      </div>
      <p className="text-sm text-foreground">
        {(i.offences ?? []).map((o) => o.offence).join('; ')}
      </p>
      <p className="text-xs text-muted-foreground">
        {day(i.occurred_on)}, {i.place}{i.vehicle ? `, ${i.vehicle}` : ''} · reported by {i.reported_by ?? 'unknown'}, {when(i.reported_at)}
        {i.requested_action !== 'record' ? ` · ${REQUESTED_LABEL[i.requested_action] ?? ''}` : ''}
      </p>

      {s && i.status === 'reported' && (
        <div className="space-y-1 rounded-md border border-border bg-muted/40 p-2.5 text-xs">
          <p className="text-foreground">{s.explanation}</p>
          {(s.offences ?? []).filter((x) => x.applies && x.basis).slice(0, 1).map((x) => (
            <p key={x.offence} className="text-muted-foreground">Read from {x.basis}.</p>
          ))}
          {(s.record_in_force ?? []).length > 0 && (
            <p className="text-muted-foreground">
              In force on the day: {(s.record_in_force ?? []).map((r) => `${r.label.toLowerCase()} for ${r.what.toLowerCase()} until ${day(r.valid_until)}`).join('; ')}.
            </p>
          )}
        </div>
      )}

      <button type="button" className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setShowDetail((v) => !v)}>
        {showDetail ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} What was reported
      </button>
      {showDetail && (
        <div className="space-y-1.5 border-l-2 border-border pl-3 text-xs">
          <p className="whitespace-pre-wrap text-foreground">{i.description}</p>
          <p className="text-muted-foreground"><span className="font-medium text-foreground">Witnesses: </span>{i.witnesses}</p>
          <p className="text-muted-foreground"><span className="font-medium text-foreground">Done straight afterwards: </span>{i.immediate_action}</p>
          {i.evidence.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {i.evidence.map((e) => (
                <Button key={e.path} size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy}
                  onClick={() => void (async () => {
                    try {
                      const x = await openFile.trigger({ path: e.path }).result
                      if (x?.url) window.open(x.url, '_blank', 'noopener')
                    } catch (err) { toast.error(writeErrorMessage(err)) }
                  })()}>
                  <FileText className="mr-1 h-3 w-3" /> {fileKind(e.mime_type)}: {e.filename}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {i.status === 'closed' && (
        <p className="text-xs text-muted-foreground">Closed by {i.closed_by ?? 'unknown'}, {when(i.closed_at)}: {i.closed_reason}</p>
      )}

      {c && (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            Released by {i.released_by ?? 'unknown'}: allegation given {day(c.notice_on)} by {NOTICE_METHODS.find((m) => m.value === c.notice_method)?.label.toLowerCase() ?? c.notice_method}.
            {c.status === 'notice_issued' ? ` His response is due by the end of ${day(c.response_due)}.` : ''}
          </p>
          {(c.response || c.no_response) && (
            <p><span className="font-medium text-foreground">His response: </span>{c.no_response ? 'none by the due date.' : c.response}</p>
          )}
          {c.status === 'hearing_referred' && (
            <p className="text-foreground">
              {c.recommendation
                ? `The chair's recommendation ${c.recommendation.ref} is filed. Release it on the Recommendations tab; that decides this incident.`
                : `Referred to a hearing. The hearing chair has the task; after the hearing the chair files the recommendation on the Recommendations tab against ${i.ref}. The most it can decide is ${c.proposed_label.toLowerCase()}.`}
            </p>
          )}
          {(c.status === 'decided' || c.status === 'withdrawn') && (
            <p><span className="font-medium text-foreground">{c.outcome_label}</span>{c.decided_by ? `, by ${c.decided_by}` : ''}, {when(c.decided_at)}{c.outcome_reasons ? `: ${c.outcome_reasons}` : ''}</p>
          )}
          {(c.events ?? []).length > 0 && (
            <>
              <button type="button" className="flex items-center gap-1 font-medium hover:text-foreground" onClick={() => setShowLog((v) => !v)}>
                {showLog ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Case log ({(c.events ?? []).length})
              </button>
              {showLog && (
                <ul className="space-y-0.5 border-l-2 border-border pl-3">
                  {(c.events ?? []).map((e, n) => <li key={n}>{when(e.at)}{e.by ? `, ${e.by}` : ''}: {e.detail}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {i.can_act && (
        <div className="flex flex-wrap gap-2 pt-1">
          {i.status === 'reported' && open === null && (
            <>
              {s?.applies && (
                <Button size="sm" variant="brand" onClick={() => setOpen('release')}><Send className="mr-1 h-3.5 w-3.5" /> Release as a written allegation</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setOpen('close')}><X className="mr-1 h-3.5 w-3.5" /> Close without a step</Button>
            </>
          )}
          {c?.status === 'notice_issued' && open === null && (
            <Button size="sm" variant="brand" onClick={() => setOpen('respond')}><MessageSquare className="mr-1 h-3.5 w-3.5" /> Record his response</Button>
          )}
          {c?.status === 'response_received' && open === null && (
            <Button size="sm" variant="brand" onClick={() => setOpen('decide')}><Gavel className="mr-1 h-3.5 w-3.5" /> Record the decision</Button>
          )}
        </div>
      )}

      {open === 'release' && i.notice_text && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <p>Give {i.driver ?? 'him'} this allegation in writing, then record how and when. That starts his two working days to respond. It decides nothing and puts nothing on his record.</p>
          <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded border border-amber-200 bg-background p-2 font-sans text-xs text-foreground dark:border-amber-900">{i.notice_text}</pre>
          <Button size="sm" variant="outline" onClick={() => void navigator.clipboard.writeText(i.notice_text ?? '').then(() => toast.success('Copied. Paste it into WhatsApp or an email to him.'))}>
            <Copy className="mr-1 h-3.5 w-3.5" /> Copy the allegation
          </Button>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`rel-on-${i.id}`}>Given to him on</Label>
              <Input id={`rel-on-${i.id}`} type="date" max={todaySast()} value={notice.notice_on} onChange={(e) => setNotice((x) => ({ ...x, notice_on: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`rel-how-${i.id}`}>How</Label>
              <NativeSelect id={`rel-how-${i.id}`} value={notice.method} onChange={(e) => setNotice((x) => ({ ...x, method: e.target.value }))}>
                {NOTICE_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </NativeSelect>
            </div>
          </div>
          <Input value={notice.note} onChange={(e) => setNotice((x) => ({ ...x, note: e.target.value }))} placeholder="Anything to note (optional)" />
          <div className="flex gap-2">
            <Button size="sm" variant="brand" disabled={busy}
              onClick={() => void run(() => release.trigger({ id: i.id, ...notice }).result, `${i.ref} released. Record his response when it comes.`)}>
              {busy && <Spinner className="mr-1 h-3.5 w-3.5" />} I gave it to him: release it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Not yet</Button>
          </div>
        </div>
      )}

      {open === 'close' && (
        <div className="space-y-2">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={s?.applies ? 'Why no step is being taken, for example: the facts do not hold up' : 'The coaching you gave him, and when'} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!reason.trim() || busy}
              onClick={() => void run(() => close.trigger({ id: i.id, reason }).result, `${i.ref} closed.`)}>Close it</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {open === 'respond' && c && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor={`resp-on-${i.id}`}>He responded on</Label>
            <Input id={`resp-on-${i.id}`} type="date" max={todaySast()} value={resp.responded_on} onChange={(e) => setResp((x) => ({ ...x, responded_on: e.target.value }))} className="sm:max-w-xs" />
          </div>
          <Textarea rows={3} value={resp.response} onChange={(e) => setResp((x) => ({ ...x, response: e.target.value }))}
            placeholder="What he said, in his words as far as you can" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="brand" disabled={!resp.response.trim() || busy}
              onClick={() => void run(() => respond.trigger({ case_id: c.id, ...resp }).result, 'His response is recorded.')}>Save his response</Button>
            <Button size="sm" variant="outline" disabled={!c.response_overdue || busy}
              title={c.response_overdue ? undefined : `He has until the end of ${day(c.response_due)}`}
              onClick={() => void run(() => respond.trigger({ case_id: c.id, no_response: true }).result, 'Recorded that he did not respond.')}>
              He did not respond in time
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {open === 'decide' && c && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-xs text-muted-foreground">
            The suggested step is {c.proposed_label.toLowerCase()}. You may decide something lighter, never heavier. Anything above a verbal warning is decided at a hearing chaired by someone who did not report or raise it.
          </p>
          <NativeSelect value={dec.decision} onChange={(e) => setDec((x) => ({ ...x, decision: e.target.value }))}>
            <option value="">Choose the decision</option>
            {decisions.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </NativeSelect>
          <Textarea rows={3} value={dec.reasons} onChange={(e) => setDec((x) => ({ ...x, reasons: e.target.value }))}
            placeholder="Your reasons, having heard him. He can ask why, and so can the CCMA." />
          {dec.decision === 'verbal_warning' && (
            <p className="text-xs text-muted-foreground">The warning goes on his record for three months and onto his records page for him to acknowledge. It is not emailed to him until driver messages for disciplinary records are switched on.</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="brand" disabled={!dec.decision || !dec.reasons.trim() || busy}
              onClick={() => void run(() => decide.trigger({ case_id: c.id, ...dec }).result,
                dec.decision === 'refer_to_hearing' ? 'Referred. The hearing chair has the task.' : 'Decision recorded.')}>
              {busy && <Spinner className="mr-1 h-3.5 w-3.5" />} Record it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </article>
  )
}
